import React, { useState, useEffect } from "react";
import { Modal, Form, InputNumber, Input, Radio, Button, Alert, Collapse, message } from "antd";
import { EnvironmentOutlined, SaveOutlined } from "@ant-design/icons";
import type { ApiProblem } from "../../../api/httpClient";
import { setGravePositionGeometry } from "./gisApi";

export interface EditPositionModalProps {
  open: boolean;
  positionId: string;
  graveCode: string;
  version: number;
  initialPosition?: {
    lon?: number | undefined;
    lat?: number | undefined;
    precisionMeters?: number | undefined;
    source?: string | undefined;
    verificationStatus?: "TEMPORARY" | "VERIFIED" | undefined;
  };
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  lon: number;
  lat: number;
  precisionMeters?: number;
  source?: string;
  verificationStatus?: "TEMPORARY" | "VERIFIED";
}

/**
 * Modal biên tập toạ độ vị trí mộ (SCR-035 / S12.5.2).
 * Tuân thủ quy tắc kiểm soát xung đột ranh giới BR-GIS và bảo vệ dữ liệu IRM.
 */
export function EditPositionModal({
  open,
  positionId,
  graveCode,
  version,
  initialPosition,
  onClose,
  onSuccess,
}: EditPositionModalProps): React.JSX.Element {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [conflictError, setConflictError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setConflictError(null);
      form.setFieldsValue({
        lon: initialPosition?.lon ?? 107.18324,
        lat: initialPosition?.lat ?? 16.54219,
        precisionMeters: initialPosition?.precisionMeters ?? 0.5,
        source: initialPosition?.source ?? "Nhập từ bản vẽ CAD (đợt 1)",
        verificationStatus: initialPosition?.verificationStatus ?? "TEMPORARY",
      });
    }
  }, [open, initialPosition, form]);

  const handleCaptureGps = () => {
    if (!navigator.geolocation) {
      message.warning("Trình duyệt không hỗ trợ dịch vụ định vị GPS.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        form.setFieldsValue({
          lon: Number(pos.coords.longitude.toFixed(6)),
          lat: Number(pos.coords.latitude.toFixed(6)),
          precisionMeters: pos.coords.accuracy ? Number(pos.coords.accuracy.toFixed(1)) : 1.0,
          source: "Đo đạc GPS thực địa",
        });
        message.success("Đã thu thập toạ độ GPS hiện trường thành công.");
      },
      (err) => {
        message.error(`Không thể lấy toạ độ GPS: ${err.message}`);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true);
    setConflictError(null);

    try {
      await setGravePositionGeometry(positionId, {
        location: {
          type: "Point",
          coordinates: [values.lon, values.lat],
        },
        expectedVersion: version,
      });

      message.success(`Đã cập nhật toạ độ cho vị trí ${graveCode} thành công.`);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const detail =
        (err as ApiProblem)?.problem?.detail ||
        (err as Error)?.message ||
        "Vi phạm quy tắc nghiệp vụ không gian (vượt ranh giới hoặc chồng lấn).";
      setConflictError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <div>
          <span className="font-bold text-slate-900">Cập nhật toạ độ vị trí mộ: </span>
          <span className="text-blue-700 font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
            {graveCode}
          </span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={520}
    >
      <div className="py-2 space-y-4">
        {conflictError && (
          <Alert
            type="error"
            showIcon
            message="Vi phạm quy tắc không gian (422)"
            description={conflictError}
            className="mb-3"
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            void handleSubmit(values);
          }}
          initialValues={{
            verificationStatus: "TEMPORARY",
          }}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-slate-700">Toạ độ vị trí (WGS 84):</span>
            <Button
              type="link"
              size="small"
              icon={<EnvironmentOutlined />}
              onClick={handleCaptureGps}
              className="text-blue-700 font-medium"
            >
              Lấy toạ độ GPS hiện trường
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="lon"
              label="Kinh độ (X)"
              rules={[{ required: true, message: "Bắt buộc nhập kinh độ" }]}
              className="mb-2"
            >
              <InputNumber
                step={0.000001}
                className="w-full font-mono"
                placeholder="107.183240"
              />
            </Form.Item>

            <Form.Item
              name="lat"
              label="Vĩ độ (Y)"
              rules={[{ required: true, message: "Bắt buộc nhập vĩ độ" }]}
              className="mb-2"
            >
              <InputNumber
                step={0.000001}
                className="w-full font-mono"
                placeholder="16.542190"
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-2">
            <Form.Item name="source" label="Nguồn toạ độ" className="mb-2">
              <Input placeholder="Ví dụ: Bản vẽ CAD đợt 1" />
            </Form.Item>

            <Form.Item name="precisionMeters" label="Sai số (mét)" className="mb-2">
              <InputNumber min={0} step={0.1} className="w-full" placeholder="0.5" />
            </Form.Item>
          </div>

          <Form.Item
            name="verificationStatus"
            label="Mức độ xác minh toạ độ"
            className="mb-3 mt-2"
          >
            <Radio.Group className="flex gap-4">
              <Radio value="TEMPORARY">Tạm thời (Chưa kiểm định)</Radio>
              <Radio value="VERIFIED">Đã kiểm định (Có biên bản)</Radio>
            </Radio.Group>
          </Form.Item>

          <Collapse
            ghost
            size="small"
            items={[
              {
                key: "technical",
                label: <span className="text-xs text-slate-500 font-medium">Thông số kỹ thuật không gian</span>,
                children: (
                  <div className="text-[11px] text-slate-600 space-y-1 bg-slate-50 p-2.5 rounded border border-slate-200">
                    <div>Hệ quy chiếu: <strong>WGS 84 (EPSG:4326)</strong></div>
                    <div>Thứ tự trục: <strong>Kinh độ (X) trước, Vĩ độ (Y) sau</strong></div>
                  </div>
                ),
              },
            ]}
          />

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 mt-4">
            <Button onClick={onClose} disabled={submitting}>
              Huỷ bỏ
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={submitting}
              className="bg-blue-700 font-bold"
            >
              Lưu toạ độ
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
}
